/* eslint-disable jsx-a11y/no-distracting-elements */
/* eslint-disable no-nested-ternary */
import CustomLink from "@Components/CustomLink";
import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";
import {
  getCommonAmenities,
  getTags,
  milesToKilometersConverter,
} from "@Pages/PropertyPage/components/PropertyDetailsSection/helper";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { AppState } from "@Store/index";
import { runTasks } from "@Utils/performanceUtils";
import {
  monthlyCountriesPriceDurationGaurd,
  PAGE,
  CANNIBALIZATION_ROLLOUT,
} from "constants/configConstants";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES, SEARCH_PAGE_CTA_VALUES } from "constants/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { memo, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "hooks/useHistory";
import BottomSection from "./components/BottomSection";
import DistanceBy from "./components/DistanceBy";
import ImageCarouselWithTags from "./components/ImageCarouselWithTags";
import InventoryDetailTags from "./components/InventoryDetailTags";
import ShortlistButton from "./components/ShortListButton";
import getLocation, {
  getDistanceData,
  getLink,
  getPricingCurrency,
  getPricingDuration,
  getPropertyFilter,
  isAvailable,
} from "./helper";
import styles from "./searchListItemV2.module.scss";
import { ISearchListItem } from "./types";
import getSortedTags from "./utils/getSortedTags";
import getSortedTagPairs from "./utils/getSortedTagPairs";
import useTranslationHook from "hooks/useTranslationHook";
import viewTransitionNavUtility from "@Utils/viewTransitionNavUtility";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useWhitelabel from "hooks/useWhitelabel";
import config from "@Config/index";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import setCookie from "helpers/setCookie";
import Image from "@Components/Image";
import { useAmberSales } from "hooks/useAmberSales";
import MarqueeText from "@Components/MarqueeText";
import useIsDesktop from "hooks/useIsDesktop";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { convertToFilterObj } from "@Pages/Search/utils/formatFilterState";
import useIsIframe from "hooks/useIsIframe";
import { getIframeSourceParam } from "@Utils/iframeContext";
import getCookie from "helpers/getCookie";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import LocationDark from "@Icons/LocationDark";
import NearbyDistanceInfo from "../NearbyDistanceInfo/NearbyDistanceInfo";
import GraduationCapThin from "@Icons/GraduationCapThin";
import { shouldShowSlashedPricing } from "helpers/slashedPricingHelper";

const SearchListItem = (props: ISearchListItem) => {
  const customWindowOpen = useCustomWindowOpen();
  const {
    images,
    name,
    location,
    meta_short,
    destination_distances,
    nearby_distances,
    regionType,
    id,
    pricing,
    feature_tags,
    features,
    index,
    canonical_name,
    regionId,
    region,
    distance_from_searched_item,
    reviews_rating,
    reviews_count,
    tags,
    page,
    showCTA = true,
    videos,
    isShortlistPage = false,
    shortlistedCardID = 0,
    hideTags = false,
    showOnlyFeaturedImage,
    isGridView,
    meta_real,
    loadedCount,
    tags_en,
    customLocation,
    whiteLabelPartnerData,
    slashed_available_price,
    nearby_distance_miles,
    provider_id,
  } = props;
  const isMapView = page === PAGE.MAP;
  const isInventoryAvailable = isAvailable(props);
  const history = useHistory();
  const isMiniProgram = useIsMiniProgram();
  const imageWrapperRef = useRef();
  const bottomRef = useRef();
  const unsortedTags = getTags(feature_tags) || [];
  const tagsArray = getSortedTags(unsortedTags) || [];
  const { display: sortedTags, en: sortedTagsEn } = getSortedTagPairs(tags, tags_en);
  const updatedTags = tagsArray?.concat(getCommonAmenities(sortedTags, features));
  const rawUpdatedTagsEn = tagsArray?.concat(getCommonAmenities(sortedTagsEn, features, true));
  const updatedTagsEn = updatedTags?.map((tag: string, i: number) => rawUpdatedTagsEn?.[i] ?? tag);
  const tagMap: any = {};
  if (tags && tags_en) {
    tags.forEach((tag: string, idx: number) => {
      tagMap[tag] = tags_en[idx].replace(/_/g, " ");
    });
  }
  // CRO tags
  const showRatings = Boolean(isInventoryAvailable && reviews_rating >= "4.0"); // P1D-3500
  const locationName = getLocation(location, [
    "route_short_name",
    "locality_long_name",
    "postal_code",
  ]);
  const distanceData = distance_from_searched_item || getDistanceData(destination_distances);

  // DEBUG: log raw nearby_distances from API
  // console.log(`[Mobile] nearby_distances RAW for ${name}:`, nearby_distances);

  const nearestUni = (nearby_distances as any[])
    ?.filter((d: any) => d.place?.toLowerCase() !== "city center")
    .reduce((nearest: any, current: any) => {
      if (!nearest) return current;
      const currentDist = parseFloat(current.distance);
      const nearestDist = parseFloat(nearest.distance);
      if (currentDist < nearestDist) return current;
      if (currentDist === nearestDist && current.place?.localeCompare(nearest.place) < 0)
        return current;
      return nearest;
    }, null) as Record<string, any> | null;

  const showDistance = page !== PAGE.ACTIVITY;

  const { userData, searchFilters, userLanguage } = useSelector(
    ({ appData, searchPageData }: AppState) => ({
      userData: appData.userData,
      searchFilters: searchPageData.filters,
      userLanguage: appData.userLanguage,
    }),
    shallowEqual,
  );

  const nearbySearchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  // Check if locality includes "near-by" to match the region name pattern
  const localityParam = nearbySearchParams.get("locality");
  const nearbyActive = localityParam ? localityParam.includes("near-by") : false;
  const showNearbyDistance = nearbyActive && nearby_distance_miles != null;
  const { isIframe, iframeSource } = useIsIframe();
  // const isOrganic = utmSource === "organic" || !utmSource;
  const isDesktop = useIsDesktop();
  const showWeChatButton = config.IS_CHINA_APP && !isDesktop && !isMiniProgram;
  const { isWhitelabel, isDemoSubdomain, partner_uuid } = useWhitelabel();
  const isTagsScrollable = true;
  const params = getPropertyFilter(convertToFilterObj(searchFilters));
  const updatedPrams = { ...params };
  if (regionId && regionId !== region.id) Object.assign(updatedPrams, { regionId });
  if (isWhitelabel && isDemoSubdomain && partner_uuid) {
    Object.assign(updatedPrams, { partner_id: partner_uuid });
  }
  // Add whitelabelv2 params if a white label partner is selected
  if (whiteLabelPartnerData && whiteLabelPartnerData?.whitelabel_domain_url) {
    Object.assign(updatedPrams, {
      whitelabelv2: true,
      utm_source: whiteLabelPartnerData.uuid,
    });
  }
  Object.assign(updatedPrams, getIframeSourceParam(isIframe, iframeSource));
  // if (regionType === "establishment") {
  //   Object.assign(updatedPrams, { regionId });
  // }
  const link = getLink(props, updatedPrams);

  const dispatch = useDispatch();
  const handlePropertyCardClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    runTasks([
      () => {
        if (regionId && !config.IS_CHINA_APP) {
          setCookie({
            cname: "searchRegionId",
            cvalue: regionId,
            exdays: region?.location?.name !== location?.locality?.long_name ? 365 : -1,
          });
        }
      },
      () => {
        gtagEventEmitter({
          action: "inventory_card_clicked",
          category: page,
          label: `${name}${location}`,
          canonical_name,
          email_id: userData?.email,
        });
      },
      () => {
        if (isWhitelabel && isIframe) {
          window.open(link, "_blank");
          return;
        }
        if (isMiniProgram) {
          // navigate to property page
          // @ts-ignore
          if (window.wx && window.wx.miniProgram) {
            const userAuthToken = getCookie("user_auth_token") || "";
            // @ts-ignore
            window.wx.miniProgram.navigateTo({
              url: `/pages/PropertyPage/PropertyPage?canonical_name=${encodeURIComponent(
                canonical_name || "",
              )}&token=${encodeURIComponent(userAuthToken)}`,
            });
          }
        } else if (isGridView) {
          customWindowOpen({ url: link, target: "_blank" });
        } else {
          viewTransitionNavUtility(() => history.push(link), "right-to-left");
        }
      },
      () => {
        const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
        propertyPageDispatcher.updateBasicDetails({ ...props, searchRegionId: regionId || "" });
      },
    ]);
  };

  const enquireFlow = useFeatureValue(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.WITHOUT_ENQUIRE,
    SEARCH_PAGE_CTA_VALUES.OFF,
  );
  const isSlashedPricingEnabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SLASHED_PRICING);

  const isEnquireDisabled =
    enquireFlow === SEARCH_PAGE_CTA_VALUES.NO_CTA && (page === PAGE.SEARCH || page === PAGE.MAP);
  const showViewProperty =
    enquireFlow === SEARCH_PAGE_CTA_VALUES.VIEW_PROPERTY_CTA && page === PAGE.SEARCH;
  const showEnquireButton = showCTA && !isEnquireDisabled;
  const { pricingPrice, pricingDuration } = monthlyCountriesPriceDurationGaurd({
    pricing,
    region,
    duration: getPricingDuration(pricing?.duration),
  });

  const { t } = useTranslationHook();

  const universityText = t("searchpage.searchlist.searchlistitem.university");
  const cityCenterText = t("searchpage.desktopmapsection.distancebymap.citycentertext");
  const fromText = t("searchpage.searchlist.searchlistitem.from");

  const getLocationText = () => {
    if (regionType === "establishment") {
      return region?.name || universityText;
    }
    if (regionType === "sublocality") {
      return region?.name;
    }
    if (customLocation && customLocation?.primary) {
      return customLocation.primary;
    }
    return cityCenterText;
  };

  // Determine the appropriate font class for property names
  const getPropertyNameFontClass = () => {
    if (isWhitelabel) {
      if (isShortlistPage) {
        return "wl-body-font";
      }
      return "wl-title-font";
    }
    return "";
  };

  const containerStyle =
    isGridView && loadedCount && loadedCount <= 10
      ? { viewTransitionName: `search-item-${id || index}` }
      : undefined;
  const customMetaForSaleOnlyChina = {
    cro_tags: {
      amber_sale: {
        offer:
          meta_real?.cro_tags?.amber_sale?.offer || meta_short?.cro_tags?.amber_sale?.offer || "",
        status: meta_short?.cro_tags?.amber_sale?.status || "",
      },
    },
  };

  // AU-only: Check for university filter
  const isAU = CANNIBALIZATION_ROLLOUT.includes(region?.location?.country?.short_name || "");
  const shouldShowNearestUni = isAU
    ? regionType !== "establishment" && !searchFilters?.university
    : regionType !== "establishment";
  const {
    isSaleOfferAvailableOnInventory,
    salesOfferTextForInventory,
    inventoryBrandImage,
    primaryColor,
    borderColor,
    backgroundGradient,
  }: any = useAmberSales({
    inventoryMeta:
      config.IS_CHINA_APP && customMetaForSaleOnlyChina ? customMetaForSaleOnlyChina : meta_short,
    page: "SEARCH",
  });

  const highlightInventory = () => {
    if (!isGridView) return;
    const event = new Event(`HOVER_INVENTORY_${id}_ADD`);
    window.dispatchEvent(event);
  };

  const highlightRemoveInventory = () => {
    if (!isGridView) return;
    const event = new Event(`HOVER_INVENTORY_${id}_REMOVE`);
    window.dispatchEvent(event);
  };

  const SaleOfferBrandTag = () => (
    <div
      className={formatClassNames(
        styles.saleOfferBannerContainer,
        isGridView && styles.gridViewWithSalesOffer,
      )}
      style={{ background: primaryColor, borderColor }}
    >
      <div
        className={formatClassNames(
          styles.imageContainer,
          isGridView && styles.imageContainerGridView,
        )}
        style={{ background: backgroundGradient }}
      >
        <Image
          src={inventoryBrandImage}
          alt="sale-offer-banner"
          width="90%"
          height="auto"
          isEagerLoad={!isMiniProgram}
        />
      </div>
      <div
        className={formatClassNames(styles.saleOfferText, isGridView && styles.gridViewOfferText)}
      >
        <MarqueeText
          text={salesOfferTextForInventory}
          startPosition="middle"
          width="100%"
          fontSize="12px"
        />
      </div>
    </div>
  );
  const showSlashPricing = shouldShowSlashedPricing({
    growthBookEnabled: isSlashedPricingEnabled,
    location,
    providerId: provider_id,
  });
  const priceComp =
    slashed_available_price && showSlashPricing ? (
      <div className={styles.slashedPriceContainerApp}>
        <div className={styles.priceSection}>
          <div>
            <span className={styles.from}>
              {config.IS_CHINA_APP
                ? ""
                : t("searchpage.components.searchlistitem.bottomsection.fromtext")}
            </span>
            {!config.IS_CHINA_APP && <span className={styles.slashPricing}>{pricingPrice}</span>}
          </div>
          <div>
            <span className={styles.price} data-testid="searchPage-inventoryCard-price">
              {config.IS_CHINA_APP && <span className={styles.slashPricing}>{pricingPrice}</span>}
              {getPricingCurrency(pricing.currency)}
              {slashed_available_price}
            </span>
            <span className={styles.duration}>/ {t(pricingDuration)}</span>
          </div>
        </div>
      </div>
    ) : (
      <div className={styles.priceContainer}>
        <span className={styles.fromText}>{t("From")}</span>
        <div>
          <span className={styles.pricingText}>{pricingPrice}/</span>
          <span className={styles.pricingDuration}>{pricingDuration}</span>
        </div>
      </div>
    );

  return (
    <div
      className={formatClassNames(
        styles.container,
        isMapView && styles.mapView,
        isGridView && styles.gridView,
      )}
      style={
        isSaleOfferAvailableOnInventory
          ? { border: `1.5px solid ${borderColor}`, ...containerStyle }
          : containerStyle
      }
      role="button"
      onKeyDown={handlePropertyCardClick}
      tabIndex={0}
      onClick={handlePropertyCardClick}
      onMouseEnter={highlightInventory}
      onMouseLeave={highlightRemoveInventory}
      id={id}
    >
      {isSaleOfferAvailableOnInventory && <SaleOfferBrandTag />}

      <div className={styles.shortlistWrap}>
        {/* Hide shortlist button for whitelabel in iframe */}
        {!(isWhitelabel && isIframe) && (
          <ShortlistButton
            inventoryId={id}
            dataTestId="mobile-searchPage-inventoryCard-shortlist-button"
            shortlistedCardID={shortlistedCardID}
            inventory={props}
            className={formatClassNames(
              styles.shortlistWrapper,
              isSaleOfferAvailableOnInventory ? styles.shortlistWrapperWithSalesOffer : "",
            )}
            screenName={APP_PAGE.SEARCH}
          />
        )}
      </div>
      <ImageCarouselWithTags
        images={images}
        videos={videos}
        index={index}
        // reviewsRating={reviews_rating}
        // reviewsCount={reviews_count}
        // showRatings={showRatings}
        inventoryData={props}
        wrapRef={imageWrapperRef}
        dataTestId="mobile-searchPage-inventoryCard-image"
        className={formatClassNames(
          styles.imgWrap,
          isSaleOfferAvailableOnInventory ? styles.imgWrapWithSalesOffer : "",
        )}
        showOnlyFeaturedImage={showOnlyFeaturedImage}
        isDesktop={false}
        isGridView={isGridView}
        isSalesOfferAvailable={isSaleOfferAvailableOnInventory}
        showRatings={showRatings}
        reviews_rating={reviews_rating}
        reviews_count={reviews_count}
      />
      <div className={styles.contentSection}>
        <CustomLink
          to={link}
          onClick={handlePropertyCardClick}
          dataTestId="searchpage-inventoryCard"
        >
          {showEnquireButton ? (
            <h2
              className={formatClassNames(getPropertyNameFontClass(), styles.heading)}
              data-testid="mobile-searchPage-inventoryCard-name"
            >
              {name}
            </h2>
          ) : (
            <div
              className={formatClassNames(
                styles.headerSection,
                page === PAGE.MAP ? styles.mapInventoryCardTitle : "",
              )}
            >
              <div className={styles.withoutEnquire}>
                <h2 className={formatClassNames(getPropertyNameFontClass(), styles.heading)}>
                  <span id={`property-card-header-span-${name}`}>{name}</span>
                </h2>
                <div className={styles.withoutEnquire_location}>
                  <span data-testid="mobile-searchPage-inventoryCard-location">{locationName}</span>
                </div>
              </div>
              {priceComp}
            </div>
          )}
        </CustomLink>
        {showEnquireButton && (
          <div id={`property-card-location-${name}`} className={styles.subHeading}>
            <span data-testid="mobile-searchPage-inventoryCard-location">{locationName}</span>
          </div>
        )}

        {(showNearbyDistance || (showDistance && distanceData?.WALKING?.distance)) && (
          <div className={styles.subContainer}>
            <div className={styles.subContainerFirstRow}>
              {!showNearbyDistance && (
                <div
                  className={styles.distanceOnMap}
                  data-testid="mobile-searchPage-inventoryCard-distance"
                >
                  <LocationDark />
                  <span className={styles.distanceCombinedText}>{`${
                    config.IS_CHINA_APP
                      ? milesToKilometersConverter(distanceData?.WALKING?.distance)
                      : distanceData?.WALKING?.distance ?? "N/A"
                  } ${fromText} ${
                    CANNIBALIZATION_ROLLOUT.includes(region?.location?.country?.short_name || "")
                      ? regionType === "establishment"
                        ? region?.name || universityText
                        : regionType === "sublocality"
                        ? region?.name
                        : (customLocation && customLocation?.primary) || cityCenterText
                      : getLocationText()
                  }`}</span>
                </div>
              )}
              {showNearbyDistance ? (
                <NearbyDistanceInfo
                  nearbyDistanceMiles={nearby_distance_miles as number}
                  regionType={regionType}
                  regionName={region?.name}
                  isGridView={isGridView}
                />
              ) : (
                <DistanceBy
                  data={distanceData}
                  regionType={regionType}
                  region={region}
                  isGridView={isGridView}
                  customLocation={customLocation}
                />
              )}
            </div>
            {nearestUni && nearestUni.distance && nearestUni.place && shouldShowNearestUni && (
              <div className={styles.nearestUniRow}>
                <GraduationCapThin />
                <span className={styles.distanceText}>
                  {`${
                    config.IS_CHINA_APP
                      ? milesToKilometersConverter(nearestUni.distance)
                      : nearestUni.distance
                  } ${fromText} ${nearestUni[`place_${userLanguage}`] || nearestUni.place}`}
                </span>
              </div>
            )}
          </div>
        )}
        {showEnquireButton && <div className={styles.divider} />}
        {!hideTags && (
          <InventoryDetailTags
            item={props}
            tags={updatedTags}
            tags_en={updatedTagsEn}
            dataTestId="mobile-searchPage-inventoryDetails-tags"
            isGridView={isGridView}
            showCashbackTag
            tagMap={tagMap}
            isScrollable={isTagsScrollable}
          />
        )}

        {showEnquireButton && (
          <BottomSection
            meta={meta_short}
            pricing={pricing}
            item={props}
            wrapRef={bottomRef}
            page={page}
            isShortlistPage={isShortlistPage}
            name={name}
            location={location}
            canonical_name={canonical_name}
            propertyLink={link}
            showViewProperty={showViewProperty}
            className={styles.btnWrap}
            showWeChatButton={showWeChatButton}
          />
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(memo(SearchListItem), {
  componentName: "SearchListItem",
  showForChina: true,
});
